import moment from 'moment';

import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { formatDateRange } from '@rover/react-lib/src/pages/search/SearchPage/components/StickyFiltersBar/utils';
import { defaultDateTimeFormatMap as DF } from '@rover/utilities/datetime';

// Daytime services are booked on days that need not be contiguous, so the query
// string carries every selected day next to the `start_date`/`end_date` range. Its
// dates are read by the server and by the links we build, so they stay on the default
// locale's ISO format rather than the viewer's.
export const LOOSE_DATES_PARAM = 'dates';

const DATES_SHOWN_IN_LABEL = 3;

const byCalendarOrder = (a: Date, b: Date): number => a.getTime() - b.getTime();

export const sortDates = (dates: Date[]): Date[] => [...dates].sort(byCalendarOrder);

export const parseLooseDates = (value?: string | null): Date[] =>
  sortDates(
    (value ?? '')
      .split(',')
      .map((rawDate) => moment(rawDate.trim(), DF.DATE_ISO, true))
      .filter((date) => date.isValid())
      .map((date) => date.toDate())
  );

export const serializeLooseDates = (dates: Date[]): string =>
  sortDates(dates)
    .map((date) => moment(date).format(DF.DATE_ISO))
    .join(',');

export const toggleLooseDate = (day: Date, dates: Date[]): Date[] => {
  const withoutDay = dates.filter((date) => !moment(date).isSame(moment(day), 'day'));
  if (withoutDay.length !== dates.length) return withoutDay;
  return sortDates([...dates, day]);
};

// Every day of a range, which is how a range coming from search reads once the
// service books its days one by one.
export const expandRangeToDates = (startDate?: Date, endDate?: Date): Date[] => {
  if (!startDate) return [];
  const dates: Date[] = [];
  const current = moment(startDate).startOf('day');
  const last = moment(endDate ?? startDate).startOf('day');

  while (current.isSameOrBefore(last)) {
    dates.push(current.toDate());
    current.add(1, 'day');
  }

  return dates;
};

const isContiguous = (sortedDates: Date[]): boolean =>
  sortedDates.every(
    (date, index) =>
      index === 0 ||
      moment(date)
        .startOf('day')
        .diff(moment(sortedDates[index - 1]).startOf('day'), 'days') === 1
  );

/**
 * Lists the first few days and counts the rest, so a long selection still fits the
 * one-line dates row. Days that happen to run back to back read as a range.
 */
export const formatLooseDates = (locale: Locale, dates: Date[]): string => {
  const sorted = sortDates(dates);
  if (sorted.length > 1 && isContiguous(sorted)) {
    return formatDateRange(sorted[0], sorted[sorted.length - 1], locale);
  }

  const shown = sorted
    .slice(0, DATES_SHOWN_IN_LABEL)
    .map((date) => formatDateRange(date, undefined, locale));
  const remaining = sorted.length - shown.length;
  return remaining > 0 ? `${shown.join(', ')} +${remaining}` : shown.join(', ');
};
